import {IReg, REG} from "lib/commons/registry";
import {BaseElementAsync, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {DOMSH} from "lib/commons/xml/domsh";
import {JSX, ShadowJsx} from "lib/commons/xml/dom";
import {HELP, helpId, IHelpDb, IHelpFrag, IHelpIndexNode, IHelpPageTpl, IHelpPageTplContext, IHelpSearchResult} from "back/help/helpApi";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {Action, IAction} from "lib/commons/actions";
import {POPUP, PopupTooltip} from "back/commons/widgets/popups";
import {IView} from "lib/commons/views";
import {ERROR} from "lib/core/errorReport";
import {Tabs} from "back/commons/widgets/tabs";
import {Grid, GridSmall, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridColDef, ICellBuilder, IRowSortFn} from "back/commons/widgets/grid-core";
import {GridDataHolderJsonArray, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {GridColTreeDef, GridDataHolderJsonTree, MxGridTreeUntouchedDatas} from "back/commons/widgets/tree";


/**
 */
export interface HelpViews extends BaseElementAsync {
	initialize(init: OHelpViewsInit): this
}

export interface OHelpViewsInit extends OSkinableInit {
	helpDbProv?: () => Promise<IHelpDb | null>

	liveView?: {
		icon?: string
		label?: string
		defaultPageId?: string
		pageTpl?: IHelpPageTpl;
		pageTplCtx?: IHelpPageTplContext;
		hideBar?: boolean
	}
	searchView?: {
		icon?: string
		label?: string
		// inScopeId?: string
		// inScopeLabel?: string
		pageTpl?: IHelpPageTpl;
		pageTplCtx?: IHelpPageTplContext;
	}
	outlineView?: {
		icon?: string
		label?: string
		indexKey?: string
		pageTpl?: IHelpPageTpl;
		pageTplCtx?: IHelpPageTplContext;
	}
}

export class HelpViews extends BaseElementAsync {
	reg: IReg<unknown>;
	helpDb?: IHelpDb

	tabs: Tabs

	liveDefaultFrag?: IHelpFrag;
	livePageTpl?: IHelpPageTpl;
	livePageTplOptions?: IHelpPageTplContext;

	liveRoot?: HTMLElement;
	liveDefaultPage?: HTMLElement;
	liveBar?: BarActions<HelpViews>;
	liveHisto?: HelpIdHisto;
	protected liveSyncLocked = false;

	searchPageTpl?: IHelpPageTpl;
	searchPageTplOptions?: IHelpPageTplContext;

	searchTextInput?: HTMLInputElement;
	searchGrid?: GridSmall;
	searchResults?: GridDataHolderJsonArray<IHelpSearchResult>;
	searchRoot?: HTMLElement;

	outlinePageTpl?: IHelpPageTpl;
	outlinePageTplOptions?: IHelpPageTplContext;

	outlineGrid?: GridSmall;
	outlineTreeData?: GridDataHelpOutline<IHelpIndexNode | IHelpFrag>;
	outlineRoot?: HTMLElement;

	protected async _initialize(init: OHelpViewsInit): Promise<void> {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg = this.findReg(init);
		this.reg.installSkin("help-views/content", sr);
		this.reg.installSkin("scroll/small", sr);
		this._initAndInstallSkin(this.localName, init);

		const msg = sr.appendChild(new MsgLabel().initialize({reg: this.reg, label: "Chargement..."}));

		let root: Node = sr;
		const countTabs = (init.liveView ? 1 : 0) + (init.searchView ? 1 : 0) + (init.outlineView ? 1 : 0);
		if (countTabs > 1) root = this.tabs = <Tabs class="noBorder"/> as Tabs;

		try {
			this.helpDb = await init.helpDbProv();
			if (this.helpDb) await this.helpDb.initIfNeeded();
			msg.remove();
		} catch (e) {
			this.helpDb = null;
			msg.setCustomMsg("Aide en ligne inaccessible", "error");
			ERROR.log("Init helpDb failed", e)
			return;
		}

		// Tab "live"
		if (init.liveView) {
			const livePane = root.appendChild(<div id="livePane" icon={init.liveView.icon || "/@skin@/help/liveTab.svg"} label={init.liveView.label || "Aide"} description="Aide contextuelle"/>)
			this.liveHisto = new HelpIdHisto();
			if (!init.liveView.hideBar) {
				getLiveActions();
				this.liveBar = livePane.appendChild(<BarActions î={{
					reg: this.reg,
					actions: getLiveActions(),
					actionContext: this
				} as OBarActionsInit<HelpViews>}/>) as BarActions<HelpViews>;
			}
			this.liveRoot = livePane.appendChild(<div id="liveRoot"/>);
			this.livePageTpl = init.liveView.pageTpl || HELP.simpleHelpPageTpl;
			this.livePageTplOptions = init.liveView.pageTplCtx || {
				reg: this.reg,
				helpDb: this.helpDb,
				openFragId: async (fragId: string, ev?: Event) => {
					//Si frag et non Topic => tooltip ?
					return this.showLiveHelpId(fragId)
				},
				openFrag: async (frag: IHelpFrag, ev?: Event) => {
					return this.showLiveHelp(frag)
				}
			}
		}

		// Tab "search"
		if (init.searchView) {
			const searchPane = root.appendChild(<div id="searchPane"
																							 icon={init.searchView.icon || "/@skin@/help/searchTab.svg"}
																							 label={init.searchView.label || "Rech."}
																							 description="Recherche dans l'aide"/>);
			this.searchTextInput = <input id="searchTextInput" type="search" placeholder="Rechercher..." spellcheck="false"
																		onchange={this.onSearchChange} oninput={this.onSearchChange} onkeydown={this.onSearchKeydown}/> as HTMLInputElement;
			searchPane.appendChild(<label id="searchText" for="searchTextInput">{this.searchTextInput}</label>);
			this.searchResults = new GridDataHolderJsonArray<IHelpSearchResult>();
			this.searchGrid = <GridSmall id="searchResults" î={{
				reg: this.reg,
				selType: "mono",
				columnDefs: [new GridColDef("searchHelp").setFlex("10em", 1, 1).setCellBuilder(new HelpSearchCellBuilder(this.helpDb))],
				dataHolder: this.searchResults,
				hideHeaders: true,
				skinScroll: 'scroll/small',
				skinOver: "help-views/grid",
				autoSelOnFocus: "first"
			} as OGridSmallInit} c-resizable=""/> as Grid;
			this.searchGrid.addEventListener("grid-select", this.onSearchSelect)
			searchPane.appendChild(this.searchGrid)
			searchPane.appendChild(<c-resizer c-orient="column"/>);
			this.searchRoot = searchPane.appendChild(<div id="searchRoot" c-resizable=""/>);
			this.searchPageTpl = init.searchView.pageTpl || HELP.withCallersHelpPageTpl;
			this.searchPageTplOptions = init.searchView.pageTplCtx || {
				reg: this.reg,
				helpDb: this.helpDb,
				openFragId: async (fragId: string, ev?: Event) => {
					//Si frag et non Topic => tooltip ?
					return this.showSearchHelpId(fragId)
				},
				openFrag: async (frag: IHelpFrag, ev?: Event) => {
					this.showSearchHelp(frag)
				}
			}
		}


		// Tab "outline"
		if (init.outlineView?.indexKey) {
			const outlinePane = root.appendChild(<div id="outlinePane"
			icon={init.outlineView.icon || "/@skin@/help/outlineTab.svg"}
			label={init.outlineView.label || "Guide"}/>);
			this.outlineTreeData = new GridDataHelpOutline<IHelpIndexNode | IHelpFrag>();
			this.outlineGrid = <GridSmall id="outlineTree" î={{
				reg: this.reg,
				selType: "mono",
				columnDefs: [new GridColTreeDef("outlineHelp").setFlex("10em", 1, 1).setCellBuilder(new HelpOutlineCellBuilder(this.helpDb))],
				dataHolder: this.outlineTreeData,
				hideHeaders: true,
				skinScroll: 'scroll/small',
				skinOver: "help-views/grid"
			} as OGridSmallInit} c-resizable=""/> as Grid;
			this.outlineGrid.addEventListener("grid-select", this.onOutlineSelect)
			outlinePane.appendChild(this.outlineGrid)
			outlinePane.appendChild(<c-resizer c-orient="column"/>);
			this.outlineRoot = outlinePane.appendChild(<div id="outlineRoot" c-resizable=""/>);
			this.outlinePageTpl = init.outlineView.pageTpl || HELP.withCallersHelpPageTpl;
			this.outlinePageTplOptions = init.outlineView.pageTplCtx || {
				reg: this.reg,
				helpDb: this.helpDb,
				openFragId: async (fragId: string, ev?: Event) => {
					return this.showOutlineHelpId(fragId)
				},
				openFrag: async (frag: IHelpFrag, ev?: Event) => {
					this.showOutlineHelp(frag)
				}
			}

		}

		if (countTabs > 1) {
			//initialize du Tabs après l'ajout des onglets.
			root.childNodes.forEach((elt) => (elt as HTMLElement).hidden = true)
			sr.appendChild(root);
		}

		//Construction du contenu "live" par défaut
		if (this.liveRoot) {
			this.liveDefaultFrag = init.liveView.defaultPageId ? await this.helpDb.getFragment(init.liveView.defaultPageId) : null;
			this.liveHisto.setCurrent(this.liveDefaultFrag);
			this.liveRoot.replaceChildren(this.liveDefaultPage = await this.makePage(this.liveDefaultFrag, this.livePageTpl, this.livePageTplOptions));
		}
		if (this.outlineTreeData) {
			const index = await this.helpDb.getIndex(init.outlineView.indexKey);
			this.outlineTreeData.setDatas(await index.getMembers(true))
		}
	}

	isLiveSyncLocked(): boolean {return this.liveSyncLocked}

	setLiveSyncLocked(st: boolean) {
		if (this.liveSyncLocked !== st) {
			this.liveSyncLocked = st;
			if (st && this._showFromSelTask) clearTimeout(this._showFromSelTask);
			this.refresh();
		}
	}

	async showLiveHelpId(id: helpId | null, showTab?: boolean) {
		if (!this.helpDb) return;
		this.showLiveHelp(await this.helpDb.getFragment(id), showTab)
	}

	async showSearchHelpId(id: helpId | null) {
		if (!this.helpDb) return;
		this.showSearchHelp(await this.helpDb.getFragment(id))
	}

	async showOutlineHelpId(id: helpId | null) {
		if (!this.helpDb) return;
		this.showOutlineHelp(await this.helpDb.getFragment(id))
	}

	async showLiveHelp(frag: IHelpFrag | null, showTab?: boolean) {
		if (!this.helpDb) return;
		if (showTab) this.tabs?.selectTab(this.tabs.findTabFromDesc(this.liveRoot))
		if (this.liveHisto.currentFrag?.fragId === frag?.fragId) return;
		this.liveHisto.setCurrent(frag);
		this.refresh();
		return this.buildLiveHelpPage(frag);
	}

	async showSearchHelp(frag: IHelpFrag | null) {
		if (!this.helpDb) return;
		return this.buildSearchHelpPage(frag);
	}

	async showOutlineHelp(frag: IHelpFrag | null) {
		if (!this.helpDb) return;
		if (frag) this.selectOutlineInGrid(this.outlineTreeData.getDatas() as (IHelpIndexNode | IHelpFrag)[], frag.fragId);
		return this.buildOutlineHelpPage(frag);
	}

	selectOutlineInGrid(lines: (IHelpIndexNode | IHelpFrag)[], fragId: helpId) {
		for (const line of lines) {
			if ("frag" in line) {
				if (line.frag.fragId === fragId) {
					this.outlineGrid.setSelectedRows(this.outlineTreeData.getOffset(line))
				} else {
					this.selectOutlineInGrid(line.members, fragId);
				}
			} else if (line.fragId === fragId) {
				this.outlineGrid.setSelectedRows(this.outlineTreeData.getOffset(line))
			}
		}
	}

	/**
	 * Affichage d'une aide isue de la sélection courante, qui sera réalisée après un certain délai.
	 *
	 * @param idProv callback pour obtenir l'id permettant de vérifier que le contexte d'appel est encore valide (isConnected..)
	 *  et de calculer l'id à visualiser (potentiellement couteux) en mode "lazy".
	 */
	async showLiveHelpFromSel(idProv: () => helpId | null) {
		if (this.liveSyncLocked) return;
		if (this._showFromSelTask) clearTimeout(this._showFromSelTask);
		this._showFromSelTask = setTimeout(() => {
			this._showFromSelTask = null;
			this.showLiveHelpId(idProv(), false);
		}, 500);
	}

	protected _showFromSelTask: any


	protected async buildLiveHelpPage(frag: IHelpFrag) {
		this.liveRoot.replaceChildren(frag === this.liveDefaultFrag ? this.liveDefaultPage : await this.makePage(frag, this.livePageTpl, this.livePageTplOptions));
	}

	protected async buildSearchHelpPage(frag: IHelpFrag) {
		this.searchRoot.replaceChildren(await this.makePage(frag, this.searchPageTpl, this.searchPageTplOptions));
	}

	protected async buildOutlineHelpPage(frag: IHelpFrag) {
		this.outlineRoot.replaceChildren(await this.makePage(frag, this.outlinePageTpl, this.outlinePageTplOptions));
	}


	protected async makePage(frag: IHelpFrag | null, tpl: IHelpPageTpl, ctx: IHelpPageTplContext): Promise<HTMLElement> {
		if (!frag) return <div/>;
		if (!this.helpDb) return new MsgLabel().initialize({reg: this.reg, label: "Aide non disponible"});
		return tpl(frag, ctx);
	}

	searchLastText: string

	protected async onSearchChange(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<HelpViews>(this);
		const text = me.searchTextInput.value.trim();
		if (text === me.searchLastText) return;
		me.searchLastText = text;
		if (text) {
			me.searchResults.setDatas(await me.helpDb.search({text: text, max: 20}));
		} else {
			me.searchResults.setDatas([]);
		}
	}

	onSearchKeydown(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === "ArrowDown") {
			const me = DOMSH.findHost<HelpViews>(this);
			if (me.searchResults.countRows() > 0) {
				me.searchGrid.setSelectedRows(0);
				me.searchGrid.focus();
				ev.stopImmediatePropagation();
				ev.preventDefault();
			}
		}
	}

	onSearchSelect(this: GridSmall, ev: CustomEvent) {
		const rowSel = this.getSelectedRow();
		if (rowSel == null) return;
		const me = DOMSH.findHost<HelpViews>(this);
		const result = me.searchResults.getRowKey(rowSel);
		if (result) me.buildSearchHelpPage(result.found);
	}

	onOutlineSelect(this: GridSmall, ev: CustomEvent) {
		const rowSel = this.getSelectedRow();
		if (rowSel == null) return;
		const me = DOMSH.findHost<HelpViews>(this);
		const entry = me.outlineTreeData.getDataByOffset(rowSel) as IHelpIndexNode | IHelpFrag;
		if (entry) me.buildOutlineHelpPage("frag" in entry ? entry.frag : entry);
	}


	protected _refresh() {
		this.liveBar?.refresh();
	}
}

REG.reg.registerSkin("help-views", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex-direction: column;
	  --tab-size: 4em;
  }

  [hidden] {
	  display: none !important;
  }

  :focus-visible {
	  outline: var(--focus-outline);
  }

  c-tabs {
	  flex: 1;
  }

  #liveRoot {
	  padding: .5em;
	  overflow: auto;
  }

  c-bar-actions {
	  margin: .3em;
	  border-bottom: 1px solid var(--border-color);
  }

  #liveForward {
	  transform: rotate(180deg);
  }

  #liveBackward:lang(ar) {
	  transform: rotate(180deg);
  }

  #liveForward:lang(ar) {
	  transform: none;
  }

  #livePane,
  #searchPane,
  #outlinePane {
	  flex: 1;
	  display: flex;
	  min-width: 0;
	  min-height: 0;
	  flex-direction: column;
  }

  #searchText {
	  display: block;
	  background: .1em / 1em no-repeat url(/@skin@/help/searchInput.svg) var(--form-search-bgcolor);
	  padding: 2px;
	  padding-inline-start: 1.2em;
	  border-bottom: 1px solid var(--border-color);
  }

  #searchTextInput {
	  padding: 2px;
	  background: var(--form-search-bgcolor);
	  color: var(--form-color);
	  border: none;
	  width: 100%;
	  font-size: inherit;
  }

  input::placeholder {
	  color: var(--fade-color);
	  letter-spacing: 2px;
	  font-size: .8em;
	  font-style: italic;
  }

  input:focus::placeholder {
	  color: transparent;
  }

  #searchResults,
  #outlineTree {
	  flex: 1 1 10em;
	  border: none;
  }


  c-resizer {
	  height: 1px;
  }

  #searchRoot,
  #outlineRoot {
	  flex: 1 1 20em;
	  padding: .5em;
	  overflow: auto;
  }
`);

REG.reg.registerSkin("help-views/content", 1, /* language=CSS */ `
	:host {
		max-width: 60vw;
		user-select: text;
	}

	.page {
		padding: .1em;
	}

	.head {
		display: flex;
		justify-content: end;
		font-style: italic;
		color: var(--fade-color);
		margin: .2em;
	}

	.headTi {
		display: block;
		padding-inline-start: 1.6em;
	}

	.tag > .headTi {
		background: url("/@skin@/help/fragTypes/tag.svg") no-repeat left / 1.5em;
	}

	.it > .headTi {
		background: url("/@skin@/help/fragTypes/it.svg") no-repeat left / 1.5em;
  }

  .concept > .headTi {
	  background: url("/@skin@/help/fragTypes/concept.svg") no-repeat left / 1.5em;
  }

  .chapter > .headTi {
	  background: url("/@skin@/help/fragTypes/chapter.svg") no-repeat left / 1.5em;
  }

  .xmlLine {
	  margin-inline-start: 2em;
	  text-indent: -2em;
  }

  .xmlLine::first-line {
	  margin-inline-start: 0;
  }

  /*.xmlFork {*/
  /*	border-inline-start: 1px solid var(--border-color);*/
  /*}*/

  /*.xmlCh {*/
  /*  margin-top: .1em;*/
  /*  margin-inline-start: 1em;*/
  /*  padding-inline-start: 1.5em;*/
  /*  background: url("/@skin@/help/xmlSub.svg") no-repeat left / 1.2em;*/
  /*}*/

  a {
	  text-decoration: underline;
	  cursor: pointer;
  }

  /* en attente d'un widget codemirror. */
  pre {
	  overflow-x: auto;
  }

  .imgInline {
	  max-height: 2em;
  }

  .imgBlock {
	  max-width: 100%;
  }
  
  code {
			background-color: #88888828;
			padding-inline: 3px;
  }

  help-zoom {
	  text-decoration: underline dotted;
  }
`);
customElements.define("help-views", HelpViews);


class HelpIdHisto {
	histo: IHelpFrag[] = [];
	currentOffset: number = 0;

	protected recurs = false;

	get currentFrag(): IHelpFrag {return this.histo[this.currentOffset]}

	hasBack() {return this.currentOffset > 0}

	hasForward() {return this.currentOffset < this.histo.length - 1}

	setCurrent(frag: IHelpFrag | null) {
		if (!frag || this.recurs) return;
		if (this.currentOffset < this.histo.length - 1) {
			//On replace la pile remontée
			const after = this.histo.slice(this.currentOffset);
			for (let i = after.length - 1; i >= 0; i--) {
				this.histo[this.currentOffset++] = after[i];
			}
		}
		this.histo.push(frag);
		if (this.histo.length > 50) this.histo.splice(0, 10); //on réduit la liste.
		this.currentOffset = this.histo.length - 1;
	}

	goBack(view: HelpViews) {
		if (this.histo.length > 0 && this.currentOffset > 0) {
			this.recurs = true;
			try {
				view.showLiveHelp(this.histo[this.currentOffset - 1]);
				this.currentOffset--;
				view.refresh();
			} finally {
				this.recurs = false
			}
		}
	}

	goForward(view: HelpViews) {
		if (this.currentOffset < this.histo.length - 1) {
			this.recurs = true;
			try {
				view.showLiveHelp(this.histo[this.currentOffset + 1]);
				this.currentOffset++;
				view.refresh();
			} finally {
				this.recurs = false
			}
		}
	}
}

let liveActions: IAction<HelpViews>[];

function getLiveActions(): IAction<HelpViews>[] {
	if (liveActions) return liveActions;

	const liveSyncLock = new Action<HelpViews>("liveSyncLock").setLabel("Bloquer la synchronisation de l'aide contextuelle avec la sélection courante").setIcon("/@skin@/help/liveSyncLock.svg");
	liveSyncLock.isToggle = () => true;
	liveSyncLock.getDatas = (api: 'toggle', ctx: HelpViews): boolean | null => ctx.isLiveSyncLocked();
	liveSyncLock.execute = (ctx: HelpViews) => ctx.setLiveSyncLocked(!ctx.isLiveSyncLocked());

	const liveBackHisto = new Action<HelpViews>("livePrev").setLabel("Aide précédente").setIcon("/@skin@/help/back.svg");
	liveBackHisto.isEnabled = (ctx: HelpViews) => ctx.liveHisto.hasBack();
	liveBackHisto.execute = (ctx: HelpViews) => ctx.liveHisto.goBack(ctx);

	const liveNextHisto = new Action<HelpViews>("liveForward").setLabel("Aide suivante").setIcon("/@skin@/help/back.svg");
	liveNextHisto.isEnabled = (ctx: HelpViews) => ctx.liveHisto.hasForward();
	liveNextHisto.execute = (ctx: HelpViews) => ctx.liveHisto.goForward(ctx);

	return liveActions = [liveSyncLock, liveBackHisto, liveNextHisto];
}

class GridDataHelpOutline<D> extends MxGridTreeUntouchedDatas(GridDataHolderJsonTree)<D> {
	constructor() {super("members")}
}

class HelpSearchCellBuilder implements ICellBuilder, IHelpPageTplContext {
	reg: IReg<unknown>;

	constructor(public helpDb: IHelpDb) {}

	redrawCell(row: GridDataRowJson<IHelpSearchResult>, root: HTMLElement): void {
		const frag = row.rowKey.found;
		if (frag.isTopic()) {
			root.className = frag.cls || "tag";
			root.replaceChildren(frag.makeTitle(null));
		} else {
			console.trace("TODO rendition help frags without title")
		}
	}

	getColSortFn(): IRowSortFn | null {return null}

	async openFragId(fragId: string, ev?: Event): Promise<void> {
		return this.openFrag(await this.helpDb.getFragment(fragId), ev);
	}

	async openFrag(frag: IHelpFrag, ev?: Event): Promise<void> {
		throw new Error("Method not implemented.");
	}
}

class HelpOutlineCellBuilder implements ICellBuilder, IHelpPageTplContext {
	reg: IReg<unknown>;

	constructor(public helpDb: IHelpDb) {}

	redrawCell(row: GridDataRowJson<IHelpFrag | IHelpIndexNode>, root: HTMLElement): void {
		const frag = "frag" in row.rowKey ? row.rowKey.frag : row.rowKey;
		if (frag.isTopic()) {
			root.replaceChildren(frag.makeTitle(null));
		} else {
			console.trace("? should be topic: ", frag)
		}
	}

	getColSortFn(): IRowSortFn | null {return null}

	async openFragId(fragId: string, ev?: Event): Promise<void> {
		return this.openFrag(await this.helpDb.getFragment(fragId), ev);
	}

	async openFrag(frag: IHelpFrag, ev?: Event): Promise<void> {
		throw new Error("Method not implemented.");
	}
}

REG.reg.registerSkin("help-views/grid", 1, /* language=CSS */ `
	.line {
		padding: .1em;
	}

	.tag {
		background: url("/@skin@/help/fragTypes/tag.svg") no-repeat left / 1em;
		padding-inline-start: 1.1em;
	}

	.it {
		background: url("/@skin@/help/fragTypes/it.svg") no-repeat left / 1em;
		padding-inline-start: 1.1em;
	}

	.concept {
		background: url("/@skin@/help/fragTypes/concept.svg") no-repeat left / 1em;
		padding-inline-start: 1.1em;
	}

	.chapter {
		background: url("/@skin@/help/fragTypes/chapter.svg") no-repeat left / 1em;
		padding-inline-start: 1.1em;
	}
`);


/**
 * Tag html pour produire un zoom sur un fragment.
 * Attribut à renseigne : helpid
 */
class HelpZoom extends HTMLElement implements IHelpPageTplContext {

	reg: IReg<unknown>;

	helpDb: IHelpDb;

	tooltipRoot: HTMLElement;

	constructor() {
		super();
		// XXX Remplacer par un floatting popup resizable au click ?
		POPUP.promiseTooltip(this,
			(owner: HTMLElement, tooltip: PopupTooltip): IView | null => {
				const fragId = this.getAttribute("helpid");
				const helpViews = DOMSH.findHost<HelpViews>(this);
				this.reg = helpViews.reg;
				this.helpDb = helpViews.helpDb;
				this.tooltipRoot = <div>
					<ShadowJsx skin="help-views/content"/>
				</div>
				helpViews.helpDb.getFragment(fragId).then((frag) => this.makeContent(frag));
				return this.tooltipRoot;
			},
			{hoverAllowed: true, anchor: {elem: this, initWidth: "35em"/*, fixSize: true FIXME bug stylage tooltip */}}
		);
	}

	async openFragId(fragId: string, ev?: Event) {
		const helpViews = DOMSH.findHost<HelpViews>(this);
		const frag = await helpViews.helpDb.getFragment(fragId);
		if (frag) return this.makeContent(frag);
	}

	async openFrag(frag: IHelpFrag, ev?: Event) {
		if (frag) return this.makeContent(frag);
	}

	async makeContent(frag: IHelpFrag) {
		const sr = this.tooltipRoot.shadowRoot;
		BASIS.clearContent(sr)
		sr.appendChild(await HELP.simpleHelpPageTpl(frag, this));
	}

}

customElements.define("help-zoom", HelpZoom);
